import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = ["input", "list", "dropzone", "deletedFiles", "error"]
  static values = {
    clipboardIcon: String,
    checkIcon: String,
    downloadIcon: String,
    trashIcon: String,
    jpgIcon: String,
    pdfIcon: String,
    wordIcon: String,
    fieldName: String,
    immediateUploadUrl: String,
    diagnosticUrl: String,
    maxFileBytes: Number
  }

  // MIME type mappings for file limit validation by type
  static fileTypeMappings = {
    'image': ['image/jpeg', 'image/png', 'image/gif', 'image/heic'],
    'pdf': ['application/pdf'],
    'doc': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'],
    'spreadsheet': ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel']
  }

  static typeKeyLabels = {
    pdf: 'PDF',
    image: 'image',
    doc: 'Word',
    spreadsheet: 'Excel'
  }

  connect() {
    // Store accumulated files in a DataTransfer object
    this.accumulatedFiles = new DataTransfer()
  }

  get acceptedTypes() {
    const accept = this.inputTarget.accept
    if (!accept) return null
    return accept.split(',').map(type => type.trim().toLowerCase())
  }

  // Slack/web downloads often arrive as application/octet-stream (or with an
  // empty type). Matching the extension against the accept list is what lets a
  // real PDF through instead of vanishing with no error.
  isFileAccepted(file) {
    const acceptedTypes = this.acceptedTypes
    if (!acceptedTypes) return true

    const fileName = file.name.toLowerCase()
    const fileType = (file.type || '').toLowerCase()
    const mimeFromExtension = this.getMimeTypeFromExtension(this.extensionOf(fileName))

    return acceptedTypes.some(accepted => {
      if (accepted === fileType) return true
      if (accepted.startsWith('.') && fileName.endsWith(accepted)) return true

      if (accepted.endsWith('/*')) {
        const baseType = accepted.slice(0, -2)
        if (fileType.startsWith(baseType + '/')) return true
        if (mimeFromExtension && mimeFromExtension.startsWith(baseType + '/')) return true
      }

      return mimeFromExtension != null && accepted === mimeFromExtension
    })
  }

  extensionOf(fileName) {
    const parts = fileName.split('.')
    if (parts.length < 2) return ''
    return parts.pop()
  }

  filterAcceptedFiles(files) {
    return Array.from(files).filter(file => this.isFileAccepted(file))
  }

  openFilePicker(event) {
    event.preventDefault()
    this.inputTarget.click()
  }

  handleFiles(event) {
    const files = event.target.files
    this.addFiles(files)
  }

  addFiles(files) {
    const limit = this.inputTarget.dataset.fileLimit ? parseInt(this.inputTarget.dataset.fileLimit) : null
    const limitMode = this.inputTarget.dataset.fileLimitMode || 'total'
    const incoming = Array.from(files)
    const acceptedFiles = incoming.filter(file => this.isFileAccepted(file))
    const rejectedTypeFiles = incoming.filter(file => !this.isFileAccepted(file))
    const { withinSize, oversized } = this.partitionBySize(acceptedFiles)
    const filesToAdd = limit ? this.trimFilesToLimit(withinSize, limit, limitMode) : withinSize
    const skippedForLimit = withinSize.length - filesToAdd.length

    this.showSelectionFeedback(rejectedTypeFiles, oversized, skippedForLimit, limit)
    this.reportDiagnostic('client_selected', {
      files: [
        ...filesToAdd.map(file => this.fileDiagnostic(file)),
        ...rejectedTypeFiles.map(file => this.fileDiagnostic(file, 'rejected_type')),
        ...oversized.map(file => this.fileDiagnostic(file, 'too_large'))
      ],
      extra: {
        accepted_count: filesToAdd.length,
        rejected_type_count: rejectedTypeFiles.length,
        oversized_count: oversized.length,
        skipped_for_limit: skippedForLimit
      }
    })

    filesToAdd.forEach(file => {
      this.accumulatedFiles.items.add(file)
    })

    this.inputTarget.files = this.accumulatedFiles.files
    this.updateList(filesToAdd)

    if (this.immediateUploadEnabled() && filesToAdd.length > 0) {
      this.uploadImmediately(filesToAdd)
    }
  }

  partitionBySize(files) {
    if (!this.hasMaxFileBytesValue || this.maxFileBytesValue <= 0) {
      return { withinSize: files, oversized: [] }
    }

    const withinSize = []
    const oversized = []
    files.forEach(file => {
      if (file.size > this.maxFileBytesValue) {
        oversized.push(file)
      } else {
        withinSize.push(file)
      }
    })
    return { withinSize, oversized }
  }

  showSelectionFeedback(rejectedFiles, oversizedFiles, skippedForLimit, limit) {
    if (!this.hasErrorTarget) return

    const messages = []
    if (rejectedFiles.length === 1) {
      messages.push(`We couldn't add ${rejectedFiles[0].name}. Please choose ${this.allowedFileLabel()}.`)
    } else if (rejectedFiles.length > 1) {
      const names = rejectedFiles.map(file => file.name).join(', ')
      messages.push(`We couldn't add these files: ${names}. Please choose ${this.allowedFileLabel()}.`)
    }

    if (oversizedFiles.length === 1) {
      messages.push(`${oversizedFiles[0].name} is larger than 50 MB and was not added.`)
    } else if (oversizedFiles.length > 1) {
      const names = oversizedFiles.map(file => file.name).join(', ')
      messages.push(`These files are larger than 50 MB and were not added: ${names}.`)
    }

    if (skippedForLimit > 0 && limit) {
      messages.push(`You can add up to ${limit} files. Some files were not added.`)
    }

    if (messages.length === 0) {
      this.errorTarget.hidden = true
      this.errorTarget.textContent = ''
      return
    }

    this.errorTarget.textContent = messages.join(' ')
    this.errorTarget.hidden = false
  }

  allowedFileLabel() {
    const labels = this.constructor.typeKeyLabels
    const keys = (this.inputTarget.dataset.allowedTypeKeys || '')
      .split(',')
      .map(key => key.trim())
      .filter(Boolean)
    const used = (keys.length ? keys : Object.keys(labels))
      .map(key => labels[key])
      .filter(Boolean)

    if (used.length === 0) return 'an allowed file'
    if (used.length === 1) {
      return used[0] === 'image' ? 'an image file' : `a ${used[0]} file`
    }
    if (used.length === 2) return `a ${used[0]} or ${used[1]} file`
    return `a ${used.slice(0, -1).join(', ')}, or ${used[used.length - 1]} file`
  }

  trimFilesToLimit(files, limit, limitMode) {
    const result = []
    
    if (limitMode === 'total') {
      const currentCount = this.accumulatedFiles.files.length + 
        this.listTarget.querySelectorAll('.file-upload-item[data-existing="true"]').length
      const availableSlots = Math.max(0, limit - currentCount)
      
      return files.slice(0, availableSlots)
    } else if (limitMode === 'per_type') {
      // Track counts per type during this selection
      const typeCountsThisSelection = {}
      
      for (const file of files) {
        const typeKey = this.getFileTypeKey(file)
        if (!typeKey) {
          // Unknown type, allow it
          result.push(file)
          continue
        }
        
        // Initialize count for this type if not already
        if (!(typeKey in typeCountsThisSelection)) {
          typeCountsThisSelection[typeKey] = 0
        }
        
        // Get existing count for this type
        const existingCount = this.countFilesByType(typeKey) + typeCountsThisSelection[typeKey]
        
        if (existingCount < limit) {
          result.push(file)
          typeCountsThisSelection[typeKey]++
        }
      }
      
      return result
    }
    
    return files
  }

  countFilesByType(typeKey) {
    // Count accumulated files of this type
    const accumulatedCount = Array.from(this.accumulatedFiles.files)
      .filter(f => this.getFileTypeKey(f) === typeKey).length
    
    // Count existing files of this type
    const existingCount = this.countExistingFilesByType(typeKey)
    
    return accumulatedCount + existingCount
  }

  canAddFile(file, limit, limitMode, allowedTypeKeys) {
    const currentFiles = Array.from(this.accumulatedFiles.files)
    const existingFiles = this.listTarget.querySelectorAll('.file-upload-item[data-existing="true"]').length
    
    if (limitMode === 'total') {
      // Total limit applies to all files combined
      return (currentFiles.length + existingFiles) < limit
    } else if (limitMode === 'per_type') {
      // Per-type limit: count files of the same type category
      const fileTypeKey = this.getFileTypeKey(file)
      if (!fileTypeKey) return true // Unknown type, allow it
      
      const sameTypeCount = currentFiles.filter(f => this.getFileTypeKey(f) === fileTypeKey).length
      const existingSameType = this.countExistingFilesByType(fileTypeKey)
      
      return (sameTypeCount + existingSameType) < limit
    }
    return true
  }

  getFileTypeKey(file) {
    const mimeType = (file.type || '').toLowerCase()
    for (const [key, mimeTypes] of Object.entries(this.constructor.fileTypeMappings)) {
      if (mimeTypes.includes(mimeType)) return key
    }

    const mimeFromExtension = this.getMimeTypeFromExtension(this.extensionOf((file.name || '').toLowerCase()))
    if (!mimeFromExtension) return null

    for (const [key, mimeTypes] of Object.entries(this.constructor.fileTypeMappings)) {
      if (mimeTypes.includes(mimeFromExtension)) return key
    }

    return null
  }

  countExistingFilesByType(typeKey) {
    const existingItems = this.listTarget.querySelectorAll('.file-upload-item[data-existing="true"]')
    let count = 0
    existingItems.forEach(item => {
      const fileName = item.querySelector('.file-info-name')?.textContent || ''
      const ext = fileName.split('.').pop()?.toLowerCase()
      const mimeType = this.getMimeTypeFromExtension(ext)
      if (mimeType && this.getFileTypeKey({ type: mimeType }) === typeKey) {
        count++
      }
    })
    return count
  }

  getMimeTypeFromExtension(ext) {
    const extToMime = {
      'jpg': 'image/jpeg', 'jpeg': 'image/jpeg', 'png': 'image/png', 'gif': 'image/gif', 'heic': 'image/heic',
      'pdf': 'application/pdf',
      'doc': 'application/msword', 'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel', 'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }
    return extToMime[ext] || null
  }

  dragOver(event) {
    event.preventDefault()
    event.stopPropagation()
  }

  dragEnter(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.add('drag-over')
  }

  dragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove('drag-over')
  }

  drop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove('drag-over')
    this.addFiles(event.dataTransfer.files)
  }

  updateList(files) {
    if (!this.hasListTarget) return

    const uploading = this.immediateUploadEnabled()

    // Add new file items
    Array.from(files).forEach((file, index) => {
      const item = document.createElement('div')
      item.classList.add('file-upload-item')
      item.dataset.newFile = 'true'
      
      // Calculate the index of this file in the accumulated files
      const fileIndex = this.accumulatedFiles.files.length - files.length + index
      
      // Create a temporary URL for the file to allow preview/download
      const fileUrl = URL.createObjectURL(file)
      
      item.dataset.fileIndex = fileIndex
      item.dataset.fileKey = this.fileKey(file)
      item.innerHTML = `
        <div class="file-item-icon">
          <img src="${this.getFileIcon(file.name)}" alt="">
        </div>
        <div class='file-item-details'>
          <div class="file-item-info">
            <span class="file-name">${this.escapeHtml(file.name)}</span>
            ${uploading
              ? `<span class="file-status uploading">Uploading</span>`
              : `<span class="file-status done"><img src="${this.checkIconValue}" alt=""> Done</span>`}
          </div>
          <div class="file-item-actions">
            <a href="${fileUrl}" download="${this.escapeHtml(file.name)}" class="file-action-btn" title="Download"><img src="${this.downloadIconValue}" alt="Download"></a>
            <button type="button" class="file-action-btn" data-action="click->file-upload#removeFile"><img src="${this.trashIconValue}" alt="Remove"></button>
          </div>
        </div>
      `
      
      this.listTarget.appendChild(item)
    })
  }

  immediateUploadEnabled() {
    return this.hasImmediateUploadUrlValue && this.immediateUploadUrlValue.length > 0
  }

  async uploadImmediately(files) {
    this.setSubmitDisabled(true)

    const formData = new FormData()
    files.forEach(file => formData.append(this.inputTarget.name, file))
    formData.append('source', 'immediate')

    try {
      const headers = {
        'Accept': 'text/vnd.turbo-stream.html'
      }
      const token = this.csrfToken()
      if (token) headers['X-CSRF-Token'] = token

      const response = await fetch(this.immediateUploadUrlValue, {
        method: 'POST',
        body: formData,
        headers,
        credentials: 'same-origin',
        redirect: 'error'
      })
      const body = await response.text()
      const turboStream = this.turboStreamBody(response, body)

      this.reportDiagnostic(response.ok && turboStream ? 'immediate_upload_succeeded' : 'immediate_upload_failed', {
        files: files.map(file => this.fileDiagnostic(file)),
        extra: { http_status: response.status }
      })

      if (response.ok && turboStream) {
        Turbo.renderStreamMessage(body)
        this.removeUploadedFiles(files)
      } else {
        this.markItemsFailed(files)
        this.showUploadFailedMessage()
      }
    } catch (error) {
      this.markItemsFailed(files)
      this.reportDiagnostic('immediate_upload_failed', {
        files: files.map(file => this.fileDiagnostic(file, 'network_error')),
        extra: { error: error.message }
      })
      this.showUploadFailedMessage()
    } finally {
      this.setSubmitDisabled(false)
    }
  }

  turboStreamBody(response, body) {
    const contentType = response.headers.get('content-type') || ''
    return contentType.includes('turbo-stream') || body.includes('<turbo-stream')
  }

  showUploadFailedMessage() {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = 'We could not save those files. Click Continue to try again.'
    this.errorTarget.hidden = false
  }

  removeUploadedFiles(files) {
    const keys = new Set(files.map(file => this.fileKey(file)))
    const remaining = new DataTransfer()
    Array.from(this.accumulatedFiles.files).forEach(file => {
      if (!keys.has(this.fileKey(file))) remaining.items.add(file)
    })
    this.accumulatedFiles = remaining
    this.inputTarget.files = this.accumulatedFiles.files

    if (!this.hasListTarget) return
    this.listTarget.querySelectorAll('.file-upload-item[data-new-file="true"]').forEach(item => {
      if (keys.has(item.dataset.fileKey)) item.remove()
    })
    this.updateFileIndices()
  }

  markItemsFailed(files) {
    if (!this.hasListTarget) return

    const keys = new Set(files.map(file => this.fileKey(file)))
    this.listTarget.querySelectorAll('.file-upload-item[data-new-file="true"]').forEach(item => {
      if (!keys.has(item.dataset.fileKey)) return
      const status = item.querySelector('.file-status')
      if (!status) return
      status.classList.remove('uploading', 'done')
      status.classList.add('failed')
      status.textContent = 'Not saved — click Continue to retry'
    })
  }

  setSubmitDisabled(disabled) {
    const submit = this.element.closest('form')?.querySelector('[type="submit"]')
    if (!submit) return
    submit.disabled = disabled
  }

  fileKey(file) {
    return [file.name, file.size, file.lastModified].join(':')
  }

  fileDiagnostic(file, reason = null) {
    return { name: file.name, type: file.type, size: file.size, reason }
  }

  reportDiagnostic(event, details = {}) {
    if (!this.hasDiagnosticUrlValue || !this.diagnosticUrlValue) return

    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
    const token = this.csrfToken()
    if (token) headers['X-CSRF-Token'] = token

    fetch(this.diagnosticUrlValue, {
      method: 'POST',
      headers,
      credentials: 'same-origin',
      body: JSON.stringify({
        event,
        files: details.files || [],
        extra: details.extra || {}
      })
    }).catch(() => {})
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  removeFile(event) {
    event.preventDefault()
    const item = event.target.closest('.file-upload-item')
    if (item) {
      const fileIndex = parseInt(item.dataset.fileIndex, 10)
      
      // Remove file from accumulated files
      if (!isNaN(fileIndex)) {
        this.removeFileAtIndex(fileIndex)
        // Update indices for remaining items
        this.updateFileIndices()
      }
      
      item.remove()
    }
  }

  removeFileAtIndex(indexToRemove) {
    const newDataTransfer = new DataTransfer()
    const files = this.accumulatedFiles.files
    
    for (let i = 0; i < files.length; i++) {
      if (i !== indexToRemove) {
        newDataTransfer.items.add(files[i])
      }
    }
    
    this.accumulatedFiles = newDataTransfer
    this.inputTarget.files = this.accumulatedFiles.files
  }

  updateFileIndices() {
    // Update data-file-index on remaining new file items
    const items = this.listTarget.querySelectorAll('.file-upload-item[data-new-file="true"]')
    items.forEach((item, index) => {
      item.dataset.fileIndex = index
    })
  }

  removeExistingFile(event) {
    event.preventDefault()
    const item = event.target.closest('.file-upload-item')
    if (item) {
      item.remove()
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  getFileIcon(filename) {
    const ext = filename.split('.').pop().toLowerCase()
    
    if (ext === 'jpg' || ext === 'jpeg') {
      return this.jpgIconValue || this.clipboardIconValue
    } else if (ext === 'pdf') {
      return this.pdfIconValue || this.clipboardIconValue
    } else if (ext === 'doc' || ext === 'docx') {
      return this.wordIconValue || this.clipboardIconValue
    }
    
    return this.clipboardIconValue
  }
}
